import React, {
  useState,
  useEffect,
  Component,
  ReactNode,
  ErrorInfo,
  useRef,
} from "react";
import { renderReport } from "lighthouse-viewer";

import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, Globe } from "lucide-react";
import SEOLoadingState from "@/components/seo/SEOLoadingState";
import SEOErrorState from "@/components/seo/SEOErrorState";
import { useBrand } from "@/contexts/brand-context";
import { pb } from "@/lib/pocketbase";
import "@/styles/lighthouse-theme.css";

// Error boundary component to catch rendering errors
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("Lighthouse viewer error:", error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}

// Interface for Lighthouse data
interface LighthouseData {
  finalUrl: string;
  fetchTime: string;
  lighthouseVersion: string;
  userAgent: string;
  categories?: {
    performance?: { score: number };
    accessibility?: { score: number };
    "best-practices"?: { score: number };
    seo?: { score: number };
    pwa?: { score: number };
  };
  audits?: Record<string, unknown>;
  // Allow other properties that might be in the response
  [key: string]: unknown;
}

// Lighthouse Report Component using the simpler renderReport approach
const LighthouseReport: React.FC<{ report: LighthouseData }> = ({ report }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current || !report) return;

    try {
      // Clear previous content
      containerRef.current.innerHTML = "";

      // Render the report
      const reportHtml = renderReport(report, {});
      containerRef.current.appendChild(reportHtml);
    } catch (error) {
      console.error("Error rendering lighthouse report:", error);
    }
  }, [report]);

  return (
    <div className="lighthouse-viewer-container w-full">
      <div className="lighthouse-viewer" ref={containerRef}></div>
    </div>
  );
};

// Cache key for storing lighthouse data in sessionStorage
const getCacheKey = (url: string) =>
  `lighthouse_data_cache_${btoa(url).slice(0, 20)}`;

// Cache expiration time (24 hours in milliseconds)
const CACHE_EXPIRATION = 24 * 60 * 60 * 1000;

const SEOPageSpeed: React.FC = () => {
  const { currentBrand, isLoading: brandLoading } = useBrand();
  const [lighthouseData, setLighthouseData] = useState<LighthouseData | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState<number | null>(null);

  useEffect(() => {
    setLighthouseData(null);
    setError(null);
    setLastFetchTime(null);

    if (currentBrand?.url && !loading) {
      const url = currentBrand.url;
      const cacheKey = getCacheKey(url);
      console.log("PageSpeed effect triggered for URL:", url);

      const cachedData = sessionStorage.getItem(cacheKey);
      if (cachedData) {
        try {
          const { data, url: cachedUrl, timestamp } = JSON.parse(cachedData);
          const now = Date.now();
          if (cachedUrl === url && now - timestamp < CACHE_EXPIRATION) {
            console.log(
              "Using cached Lighthouse data, expires in:",
              Math.round((timestamp + CACHE_EXPIRATION - now) / 1000 / 60),
              "minutes",
            );
            setLighthouseData(data);
            setLastFetchTime(timestamp);
            return;
          } else {
            console.log("Cache expired or URL mismatch, fetching new data");
            sessionStorage.removeItem(cacheKey);
          }
        } catch (e) {
          console.error("Error parsing cached data:", e);
          sessionStorage.removeItem(cacheKey);
        }
      }

      const fetchPageSpeedData = async () => {
        console.log("Fetching PageSpeed data for:", url);
        setLoading(true);
        setError(null);
        try {
          const response = await fetch(
            `${pb.baseURL}/api/v1/pagespeed?url=${encodeURIComponent(url)}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: pb.authStore.token,
              },
            },
          );
          if (!response.ok) {
            if (response.status === 401) {
              throw new Error(
                "Authentication required. Please log in and try again.",
              );
            }
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const data = await response.json();
          console.log("Lighthouse data received:", data);
          const timestamp = Date.now();
          sessionStorage.setItem(
            cacheKey,
            JSON.stringify({ data, url, timestamp }),
          );
          setLastFetchTime(timestamp);
          if (data && typeof data === "object") {
            if (data.lighthouseResult) {
              setLighthouseData(data.lighthouseResult);
            } else {
              setLighthouseData(data);
            }
          } else {
            throw new Error("Invalid lighthouse data format");
          }
        } catch (e: unknown) {
          if (e instanceof Error) {
            setError(e.message);
          } else {
            setError("An unknown error occurred");
          }
        } finally {
          setLoading(false);
        }
      };
      fetchPageSpeedData();
    }
  }, [currentBrand?.url]);

  if (brandLoading) {
    return <SEOLoadingState message="Loading brand data..." />;
  }

  if (!currentBrand?.url) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>No Data Available</AlertTitle>
        <AlertDescription>
          Please select a brand to start an audit.
        </AlertDescription>
      </Alert>
    );
  }

  const formatTimestamp = (timestamp: number | null) => {
    if (!timestamp) return "";
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div className="container space-y-6 py-6">
      <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            PageSpeed Insights
          </h1>
          <p className="text-muted-foreground mt-2">
            Comprehensive performance analysis powered by Google Lighthouse
          </p>
        </div>
        <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-x-2 sm:space-y-0">
          <Badge variant="outline" className="text-sm">
            <Globe className="h-3 w-3 mr-1" />
            {currentBrand.url}
          </Badge>
          {lastFetchTime && (
            <Badge variant="secondary" className="text-sm">
              Last updated: {formatTimestamp(lastFetchTime)}
            </Badge>
          )}
        </div>
      </div>
      {loading && <SEOLoadingState message="Fetching Lighthouse report..." />}
      {error && (
        <SEOErrorState title="Failed to Fetch Report" message={error} />
      )}
      {lighthouseData && (
        <div className="w-full">
          <ErrorBoundary
            fallback={
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error Rendering Lighthouse Report</AlertTitle>
                <AlertDescription>
                  There was an error rendering the lighthouse report. The data
                  format may not be compatible.
                </AlertDescription>
              </Alert>
            }
          >
            <div className="lighthouse-dashboard-theme bg-card border border-border rounded-xl shadow-sm overflow-hidden">
              <LighthouseReport report={lighthouseData} />
            </div>
          </ErrorBoundary>
        </div>
      )}
    </div>
  );
};

export default SEOPageSpeed;
